import { ComponentChildren } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import ButtonToggle from '@bonniernews/dn-design-system-web/components/button-toggle/button-toggle.tsx'
import { BylineData } from '@bonniernews/dn-design-system-web/components/byline/byline.tsx'
import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

export interface BylineListProps extends SharedProps {
  bylines: BylineData[]
  showBorderOnLastItem?: boolean
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/byline-list](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/byline-list)
 * - Storybook: [Byline-list](https://designsystem.dn.se/?path=/docs/article-byline-list--docs)
 *
 * BylineList is a component that renders a list of bylines with optional media and titles. It is used for the sheet that displays if we click a byline with more than one author.
 *
 * **This is only implemented in tsx. Nkj files are not implemented. They are still using the old list item component.**
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/byline-list/byline-list.scss'`
 */
export const BylineList = ({
  bylines,
  attributes,
  showBorderOnLastItem = false,
  forcePx,
  classNames,
}: BylineListProps) => {
  const componentClassName = 'ds-byline-list__item'

  const classes = formatClassString(['ds-byline-list', forcePx && 'ds-force-px', classNames])

  return (
    <ul className={classes} {...attributes}>
      {bylines &&
        bylines.map((byline, index) => {
          const { name, authorId, authorPageUrl, bylineImage, role, followable, followButtonAttributes } = byline
          const linkAttributes: { [key: string]: string } = {
            'data-link-category': 'article author',
            'data-index': `${index}`,
            'data-label': name,
            'data-id': authorId,
          }

          const shouldShowBorder = showBorderOnLastItem || index !== bylines.length - 1

          return (
            <BylineListInner
              key={`byline-list-${index}`}
              {...{ ...byline, forcePx }}
              linkAttributes={linkAttributes}
              border={shouldShowBorder}
              followable={followable}
              authorPageUrl={authorPageUrl}
              followButtonAttributes={followButtonAttributes}
            >
              {bylineImage && <div className={`${componentClassName}__portrait`}>{bylineImage}</div>}
              <Title
                title={name}
                role={role}
                authorPageUrl={followable ? authorPageUrl : ''} // only followable items should have linked titels, else the whole item is a link
                attributes={followable && authorPageUrl ? linkAttributes : undefined} // If byline is a link we set these on the wrapping element
              />
            </BylineListInner>
          )
        })}
    </ul>
  )
}

interface BylineListInnerProps {
  disabled?: boolean
  attributes?: { [key: string]: string }
  listItemClassNames?: string
  linkAttributes?: { [key: string]: string }
  buttonClassNames?: string
  authorPageUrl?: string
  followable?: boolean
  followed?: boolean
  followButtonAttributes?: { [key: string]: string }
  border?: boolean
  forcePx?: boolean
  children: ComponentChildren
}

const BylineListInner = ({
  disabled,
  attributes,
  linkAttributes,
  authorPageUrl,
  listItemClassNames,
  followButtonAttributes,
  buttonClassNames,
  followable,
  followed,
  border = true,
  forcePx,
  children,
}: BylineListInnerProps) => {
  const componentClassName = 'ds-byline-list__item'
  const classes = formatClassString([componentClassName, listItemClassNames, border && `${componentClassName}--border`])
  const isLink = authorPageUrl && !followable

  return (
    <li className={classes} {...attributes}>
      {isLink ? (
        <a href={authorPageUrl} className={`${componentClassName}__content`} {...linkAttributes}>
          {children}
          {<span className={`${componentClassName}__icon-right`}>{IconUse({ iconName: 'arrow_forward' })}</span>}
        </a>
      ) : (
        <div className={`${componentClassName}__content`}>
          {children}
          {followable && (
            <ButtonToggle
              {...{
                text: 'Följ',
                selectedText: 'Följer',
                variant: 'secondaryFilled',
                size: 'sm',
                selected: followed,
                disabled,
                forcePx,
                attributes: followButtonAttributes,
                classNames: `${buttonClassNames || ''} watch--follow`,
              }}
            />
          )}
        </div>
      )}
    </li>
  )
}
interface TitleProps {
  title?: string
  role: string
  authorPageUrl?: string
  attributes?: { [key: string]: string }
}

const Title = ({ title, role, authorPageUrl, attributes }: TitleProps) => {
  const componentClassName = 'ds-byline-list__item'
  const titleClasses = formatClassString([`${componentClassName}__title`])

  return (
    <div className={`${componentClassName}__titles`}>
      {authorPageUrl ? (
        <a href={authorPageUrl} className={titleClasses} {...attributes}>
          {title}
        </a>
      ) : (
        <span className={titleClasses} {...attributes}>
          {title}
        </span>
      )}
      <span className={`${componentClassName}__role`}>{role}</span>
    </div>
  )
}
